<template lang="pug">
  .wrapper-edit-routine(v-shortkey.once="shortcuts" @shortkey="shortcurtsHandler")
    h2 {{ id ? 'editar ' + $ws('routine') : 'adicionar ' + $ws('routine') }}

    Validate(v-model="name" :minLength="4")
      PTextField(label="nome da escala" v-focus ref="name")
    Validate(v-model="code" onlyNumbers)
      PTextField(label="código")

    label.esquema
      span esquema:
      PTextField(:customVmodel="customSetterEsquema('workDays')" label=" " v-mask="'##'")
      span x
      PTextField(:customVmodel="customSetterEsquema('offDays')" label=" " v-mask="'##'")

    .on-call-activator
      div(style="width: max-content")
        PCheckbox(v-if="hasOnCall" v-model="useOnCall" label="adicionar sobre aviso")

    .buttons
      PButton(color="success" size="md" @click="save" :loading="saving") {{ !id ? 'adicionar' : 'salvar' }}
      PButton(color="danger" size="md" @click="goToList") cancelar

    .journeys
      .jrn-container(
        v-for="(i, index) in journeys"
        :class="{editing: indexesBeingEdited.includes(index)}"
        @click.ctrl="editDay($event, index)"
      )
        .jrn
          span {{index+1}}º dia
          PButton(
            :color="i.color"
            :title="i.name"
            size="md"
            @click="editDay($event, index)"
            block
          )
            | {{ rightJourneyTextAndColor(i).text }}

          PButton(
            v-if="useOnCall"
            color="warning"
            :title="i.name"
            size="md"
            @click="editDay($event, index, 'oncall')"
            block
          )
            | {{ onCall[index].name }}

    .picker(:class="{disabled: indexesBeingEdited.length === 0}")
      RadioSlider(
        :items="modes",
        :current="modes.findIndex(i => i.id === modeJourney.selected.id)",
        @selected="changedModeJourney"
      )

      .tables
        JourneyPicker(v-if="modeJourney.selected.id !== 'oncall'" @selectJourney="selectJourney"
          :perPage="10" ref="picker", :kind="modeJourney.selected.id")

        OnCallPicker(v-if="modeJourney.selected.id === 'oncall'", @selectedOnCall="selectOnCall")

</template>

<script>
import { range, rightJourneyTextAndColor } from 'src/utils'
import { setAmplitudeEvent } from 'src/amplitudeUtils'
import JourneyPicker from 'src/common/JourneyPicker'
import OnCallPicker from 'src/common/OnCallPicker'
import { useValidationsMixin } from 'src/common/validations'
import { ErroTiposDeJornadasNaoCoincidemComEsquema } from '../errors'
import { escalaEstaValida, montarEscala, pegarJornada } from '../../../utils'

export default {
  name: 'RoutineDetails',
  components: {JourneyPicker, OnCallPicker},
  mixins: [useValidationsMixin({deepCheck: true})],
  props: {
    id: {type: String, required: false, default: null}
  },
  created () {
    if (this.id) {
      this.loadRoutineInfo()
    } else {
      this.genDefaultOnCall()
      this.genDefaultJourneys()
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.$refs.name.focus()
    })
  },
  watch: {
    indexesBeingEdited (newValue) {
      if (newValue.length) {
        this.focusTable()
      }
    }
  },
  data () {
    return {
      saving: false,
      shortcuts: {
        esc: ['esc']
      },
      indexesBeingEdited: [],
      name: '',
      code: '',
      workDays: 5,
      offDays: 2,
      journeys: [],
      onCall: [],
      useOnCall: false,
      modeJourney: {
        selected: { name: 'trabalho', id: 'workday' },
        items: [
          { name: 'trabalho', id: 'workday' },
          { name: 'sem trabalho', id: 'offday' }
        ]
      }
    }
  },
  computed: {
    modes () {
      const items = [...this.modeJourney.items]

      if (this.useOnCall) {
        items.push({name: 'sobreaviso', id: 'oncall'})
      }
      return items
    },
    hasOnCall () {
      return this.$store.state.userInfo.compMan.hasOnCall
    },
    loadedJourneys () {
      return this.$store.state.common.journeys.items
    },
    loadedOnCall () {
      return this.$store.state.common.oncall.items
    }
  },
  methods: {
    validarTiposDeJornadasCoincideComEscala () {
      const diasDeTrabalho = this.workDays
      const diasDeFolga = this.offDays
      const jornadas = this.journeys

      const jornadasContadasPorTipo = [...jornadas].reduce((acumulador, jornada) => {
        if (jornada.kind === 0) acumulador.trabalho += 1
        if (jornada.kind === -1) acumulador.folga += 1

        return acumulador
      }, {trabalho: 0, folga: 0})

      const numeroDeJornadasDeTrabalhoCoincideComEsquema = jornadasContadasPorTipo.trabalho === diasDeTrabalho

      const numeroDeJornadasDeFolgaCoincideComEsquema = jornadasContadasPorTipo.folga === diasDeFolga

      const tiposDeJornadasCoincidemComEsquema = numeroDeJornadasDeTrabalhoCoincideComEsquema && numeroDeJornadasDeFolgaCoincideComEsquema

      if (!tiposDeJornadasCoincidemComEsquema) throw new ErroTiposDeJornadasNaoCoincidemComEsquema()
    },
    rightJourneyTextAndColor,
    shortcurtsHandler (event) {
      if (event.srcKey === 'esc') {
        this.indexesBeingEdited = []
      }
    },
    selectJourney (journey) {
      this.indexesBeingEdited.forEach(i => {
        this.journeys[i] = journey
      })
      this.indexesBeingEdited = []
    },
    selectOnCall (onCall) {
      this.indexesBeingEdited.forEach(i => {
        this.onCall[i] = onCall
      })
      this.indexesBeingEdited = []
    },
    editDay (event, index, goToMode = 'workday') {
      if (event.shiftKey && this.indexesBeingEdited.length) {
        const lastItemSelected = this.indexesBeingEdited[this.indexesBeingEdited.length - 1]

        const list = lastItemSelected < index ? range(lastItemSelected, index) : range(index, lastItemSelected)

        if (lastItemSelected > index) {
          list.reverse()
        }

        this.indexesBeingEdited = list
        return
      }

      if (this.indexesBeingEdited.includes(index)) {
        this.indexesBeingEdited = this.indexesBeingEdited.filter(i => i !== index)
      } else {
        this.indexesBeingEdited = [...this.indexesBeingEdited, index]
      }

      this.modeJourney.selected = this.modes.find(i => i.id === goToMode)
    },
    calcNumberOfJourneys () {
      const newNumber = this.workDays + this.offDays

      if (newNumber > 0) {
        const firstWork = this.loadedJourneys.find(i => i.kind === 0)
        const folga = this.loadedJourneys.find(i => i.name.includes('Folga'))

        this.journeys = [
          ...range(0, this.workDays - 1).map(i => firstWork),
          ...range(0, this.offDays - 1).map(i => folga)
        ]
      } else {
        this.journeys = []
      }
      this.genDefaultOnCall()
    },
    focusTable () {
      try {
        this.$refs.picker.$refs.table.focusSearch()
      } catch (e) {}
    },
    changedModeJourney (item) {
      this.modeJourney.selected = item
      this.focusTable()
    },
    async genDefaultJourneys () {
      const [jornadaDeTrabalho, jornadaDeFolga] = await pegarJornada()
      if (escalaEstaValida(jornadaDeTrabalho, jornadaDeFolga)) {
        this.journeys = montarEscala(jornadaDeTrabalho, jornadaDeFolga)
      } else {
        this.$modalDeErro('Para continuar com esta operação é necessário que existam jornadas cadastradas no sistema')
      }
    },
    async genDefaultOnCall () {
      if (this.hasOnCall) {
        await this.$store.dispatch('common/oncall/fetchItems')
        const firstNoWork = this.loadedOnCall.find(i => i.name === 'Sem Sobreaviso')

        this.onCall = range(0, (this.workDays + this.offDays) - 1).map(i => firstNoWork)
      }
    },
    customSetterEsquema (type) {
      return {
        get: () => {
          return this[type]
        },
        set: (val) => {
          val = parseInt(val)

          if (val < 39) {
            this[type] = val
            this.calcNumberOfJourneys()
          }
        }
      }
    },
    async loadRoutineInfo () {
      this.$store.dispatch('common/journeys/fetchItems')
      if (this.hasOnCall) {
        this.$store.dispatch('common/oncall/fetchItems')
      }

      const result = await this.$API.routine.get({id: this.id})
      const {name, code, journeys, workDays, offDays, onCallJourneys} = result.data.success[0]

      this.name = name
      this.code = code
      this.journeys = journeys
      this.workDays = workDays
      this.offDays = offDays
      if (onCallJourneys && onCallJourneys.length) {
        this.useOnCall = true
        this.onCall = onCallJourneys
      }
    },
    async save () {
      if (!this.isAllValid()) {
        this.displayAllErrors()
        return
      }
      this.saving = true

      try {
        this.validarTiposDeJornadasCoincideComEscala()
        await this.$API.routine.save({
          name: this.name,
          code: this.code,
          id: this.id,
          journeys: this.journeys,
          offDays: this.offDays,
          onCallJourneys: this.useOnCall ? this.onCall : [],
          workDays: this.workDays
        })
        this.$modalDeSucesso('Escala salva com sucesso!')
        setAmplitudeEvent('salva escala', {})
        this.$router.push('/routine/list')
      } catch (e) {
        if (e instanceof ErroTiposDeJornadasNaoCoincidemComEsquema) {
          this.$modalDeErro(e.message)
        } else {
          this.$modalDeErro(e?.data?.error ? e.data.error : 'Não foi possível salvar a ' + this.$ws('routine'))
        }
      }
      this.saving = false
    },
    goToList () {
      this.$router.push('/routine/list')
    }
  }
}
</script>

<style scoped lang="scss">
.wrapper-edit-routine {
  display: grid;
  grid-template-columns: 2fr 1fr auto auto;
  grid-column-gap: 30px;
  grid-row-gap: 10px;

  h2 {
    grid-row: 1;
    grid-column: 1/5;
    text-align: center;
  }

  div {
    align-self: flex-end;
  }

  .buttons {
    margin-bottom: 13px;
    display: grid;
    grid-auto-flow: column;
    column-gap: 1px;
  }

  .esquema {
    display: grid;
    grid-template-columns: 100px 50px 50px 50px;
    align-items: center;

    span:first-child {
      padding-top: 20px;
    }

    span:not(:first-child) {
      width: 100%;
      padding-top: 20px;
      text-align: center;
    }
  }

  .on-call-activator {
    grid-row: 3;
    grid-column: 1/5;
  }

  .journeys {
    margin-bottom: 20px;
    display: grid;
    grid-column: 1/5;
    text-align: center;
    grid-template-columns: repeat(7, 1fr);
    grid-column-gap: 20px;
    margin-left: -20px;
    width: calc(100% + 40px);

    .jrn-container {
      padding: 10px 20px 10px 20px;

      &.editing {
        background-color: lightgrey;
      }

      .jrn {
        display: grid;
        grid-row-gap: 10px;
        align-items: center;
      }
    }
  }
  .picker {
    grid-column: 1/5;
    margin-bottom: 20px;
    transition: opacity 0.5ms linear;

    .tables {
      margin-top: 20px;
    }

    &.disabled {
      opacity: 0.3;
      pointer-events: none;
    }
  }
}
</style>
